import React from 'react';
import { Icon, IconEnum } from '@investoro/core';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import type { HistogramBarDto, MarketContextSectionDto } from 'utils/types/AiPredictorModels';

import { SectionCard, SectionHeader, SectionTitle, TextContent } from 'components/valuation/common';

const HistogramsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const HistogramLabel = styled.div`
    font-size: var(--font-size-body-3);
    font-weight: 600;
    color: var(--color-primary-70);
    margin-bottom: 8px;
`;

const HistogramWrapper = styled.div`
    width: 100%;
    height: 220px;
`;

interface HistogramChartProps {
    label: string;
    data: HistogramBarDto[];
}

const HistogramChart = ({ label, data }: HistogramChartProps) => {
    return (
        <div>
            <HistogramLabel>{label}</HistogramLabel>
            <HistogramWrapper>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                        <XAxis dataKey="rangeLabel" tick={{ fontSize: 11, fill: 'var(--color-primary-70)' }} />
                        <YAxis tick={{ fontSize: 11, fill: 'var(--color-primary-70)' }} allowDecimals={false} />
                        <Tooltip
                            formatter={(value: number) => [value, 'Liczba ofert']}
                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                        />
                        <Bar dataKey="offerCount" radius={[
                            4,
                            4,
                            0,
                            0
                        ]} barSize={40}>
                            {data.map((entry) => <Cell
                                key={entry.rangeLabel}
                                fill={entry.isSubjectPropertyRange ? 'var(--color-alt)' : 'var(--color-alt-second-10)'}
                            />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </HistogramWrapper>
        </div>
    );
};

interface ValuationMarketContextProps {
    section: MarketContextSectionDto;
}

const ValuationMarketContext = ({ section }: ValuationMarketContextProps) => {
    if (!section.histogramData || section.histogramData.length === 0) return null;

    return (
        <SectionCard>
            <SectionHeader>
                <Icon icon={IconEnum.CHART} />
                <SectionTitle>Rozkład cen w okolicy</SectionTitle>
            </SectionHeader>
            {section.sectionDescription &&
                <TextContent>{section.sectionDescription}</TextContent>
            }
            <HistogramsGrid>
                <HistogramChart label="Rozkład cen sprzedaży" data={section.histogramData} />
                {section.areaHistogramData && section.areaHistogramData.length > 0 &&
                    <HistogramChart label="Powierzchnia sprzedawanych mieszkań" data={section.areaHistogramData} />
                }
            </HistogramsGrid>
        </SectionCard>
    );
};

export default ValuationMarketContext;
